import { NavLink, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { LifeBuoy, LogOut, Lock } from "lucide-react"
import { useEntitlements } from "@/hooks/use-entitlements"
import { useAuth } from "@/components/auth-provider"
import { useBrand } from "@/hooks/use-brand"
import {
  APP_NAV_GROUPS,
  APP_NAV_DASHBOARD,
  APP_NAV_SETTINGS,
  navTestId,
} from "@/config/app-navigation"

function NavItem({ item, allowed, label }) {
  const baseClass =
    "flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] transition-colors min-w-0"
  const iconClass = "h-4 w-4 shrink-0 opacity-80"

  if (!allowed) {
    return (
      <Link
        to="/app/settings?tab=billing"
        data-testid={navTestId(item.to)}
        className={`${baseClass} text-muted-foreground/55 hover:text-muted-foreground`}
        title="Upgrade necesar"
      >
        <Lock strokeWidth={1.5} className={iconClass} />
        <span className="truncate">{label}</span>
      </Link>
    )
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={navTestId(item.to)}
      className={({ isActive }) =>
        `${baseClass} ${
          isActive
            ? "bg-primary/10 text-foreground font-medium [&_svg]:opacity-100"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
        }`
      }
    >
      <item.icon strokeWidth={1.5} className={iconClass} />
      <span className="truncate">{label}</span>
    </NavLink>
  )
}

function NavSection({ group, can, t }) {
  const items = group.items.map((item) => ({
    item,
    label: t(item.labelKey),
    allowed: can(item.feature),
  }))
  return (
    <div className="mt-5 first:mt-0">
      <p className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
        {t(group.labelKey)}
      </p>
      <ul className="space-y-0.5">
        {items.map(({ item, label, allowed }) => (
          <li key={item.to}>
            <NavItem item={item} allowed={allowed} label={label} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function AppSidebar({ onSignOut }) {
  const { t } = useTranslation()
  const brand = useBrand()
  const { user } = useAuth()
  const { user: entUser, hasFeature: can } = useEntitlements()

  const dashboardLabel = t(APP_NAV_DASHBOARD.labelKey)
  const settingsLabel = t(APP_NAV_SETTINGS.labelKey)

  return (
    <aside
      className="hidden lg:flex w-[15.5rem] shrink-0 border-r border-border flex-col bg-card/30"
      data-testid="app-sidebar"
    >
      <Link
        to="/"
        className="px-5 h-14 flex items-center gap-2.5 border-b border-border min-w-0 shrink-0"
        data-testid="app-brand-link"
      >
        <span className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-bold text-sm shrink-0">
          A
        </span>
        <span className="font-display text-sm tracking-tight truncate">{brand.brand}</span>
      </Link>

      {user && (
        <div className="px-4 py-3 border-b border-border shrink-0" data-testid="sidebar-practice">
          <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-muted-foreground/80">
            {t("app.practice")}
          </p>
          <p className="mt-0.5 text-sm font-medium truncate leading-snug">{user.practice_name}</p>
          {entUser?.effective_plan && (
            <p className="text-[10px] text-muted-foreground mt-0.5 capitalize">
              Plan {entUser.effective_plan}
              {entUser.subscription_status === "trialing" ? " · probă" : ""}
            </p>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 min-h-0">
        <div className="mb-1">
          <NavItem
            item={APP_NAV_DASHBOARD}
            allowed={can(APP_NAV_DASHBOARD.feature)}
            label={dashboardLabel}
          />
        </div>

        {APP_NAV_GROUPS.map((group) => (
          <NavSection key={group.id} group={group} can={can} t={t} />
        ))}

        <div className="mt-5 pt-4 border-t border-border/80">
          <NavItem
            item={APP_NAV_SETTINGS}
            allowed={can(APP_NAV_SETTINGS.feature)}
            label={settingsLabel}
          />
        </div>
      </nav>

      <div className="shrink-0 p-3 border-t border-border bg-card/40">
        <div className="rounded-lg bg-muted/50 px-3 py-2.5">
          <p className="text-xs font-medium">{t("app.needHelp")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 mb-2 leading-relaxed">{t("app.helpBody")}</p>
          <Link
            to="/help"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary"
          >
            <LifeBuoy strokeWidth={1.5} className="h-3.5 w-3.5" />
            {t("app.contactSupport")}
          </Link>
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-2 w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-muted/80 text-left"
          data-testid="signout-btn"
        >
          <LogOut strokeWidth={1.5} className="h-4 w-4 shrink-0" />
          {t("app.signOut")}
        </button>
      </div>
    </aside>
  )
}
